import { type QuoteDocument, type User } from '@kalos/kalos-rpc';
import {
  Badge,
  Button,
  Card,
  CardContent,
  DataTablePagination,
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  Skeleton,
} from '@kalos/ui';
import { format, parseISO } from 'date-fns';
import { ChevronDown, EyeIcon, FileTextIcon } from 'lucide-react';
import { type FC, useMemo, useState } from 'react';

import { NULL_TIME } from '../../../../constants';
import { useQuoteLineQuery } from '../../../../hooks/react-query/useQuoteLineQuery';
import {
  computeApprovedProposalTotal,
  proposalPricingFromQuoteDocument,
} from '../../../../utils/proposalApprovedTotal';
import { ProposalDetailsView } from './ProposalDetailsView';

interface PastProposalsProps {
  proposalsData?: { results: QuoteDocument[]; totalCount: number };
  isLoading?: boolean;
  error?: Error | null;
  jobCustomer?: User | null;
  currentPage: number;
  onPageChange: React.Dispatch<React.SetStateAction<number>>;
  pageCount: number;
  /** 'compact' renders single-row proposals to match the scheduler Review step. */
  variant?: 'default' | 'compact';
}

interface ProposalCardProps {
  proposal: QuoteDocument;
  onViewProposal: (proposalId: number) => void;
  customer?: User | null;
  compact?: boolean;
}

interface ProposalDetailsDialogProps {
  proposal: QuoteDocument;
  onClose: () => void;
}

const ProposalDetailsDialog: FC<ProposalDetailsDialogProps> = ({ proposal, onClose }) => {
  return (
    <Dialog open onOpenChange={onClose}>
      <DialogContent className="max-h-[90vh] max-w-3xl overflow-y-auto bg-white">
        <DialogHeader>
          <DialogTitle className="text-lg">Proposal P-{proposal.id}</DialogTitle>
        </DialogHeader>
        <ProposalDetailsView proposal={proposal} />
      </DialogContent>
    </Dialog>
  );
};

const ProposalCard: FC<ProposalCardProps> = ({
  proposal,
  onViewProposal,
  customer,
  compact = false,
}) => {
  const [open, setOpen] = useState(false);
  // Fetch quote lines for this specific proposal using jobNumber (eventId)
  const { data: quoteLinesData, isLoading: isLoadingQuoteLines } = useQuoteLineQuery({
    filters: {
      quoteDocumentId: proposal.id,
      isActive: 1,
      withoutLimit: true,
    },
    enabled: !!proposal.id,
  });

  const { approvedTotal, approvedCount } = useMemo(() => {
    if (!quoteLinesData?.results) return { approvedTotal: 0, approvedCount: 0 };
    return computeApprovedProposalTotal(
      quoteLinesData.results,
      proposalPricingFromQuoteDocument(proposal),
    );
  }, [quoteLinesData?.results, proposal]);

  const signedDate =
    proposal.signedDate && proposal.signedDate !== NULL_TIME
      ? format(parseISO(proposal.signedDate), 'M/d/yyyy')
      : null;
  const sentDate = proposal.quoteSentDate && proposal.quoteSentDate !== NULL_TIME;
  const createdDate = proposal.createdDate
    ? format(parseISO(proposal.createdDate), 'MMM d, yyyy')
    : 'N/A';

  const getStatusBadge = () => {
    const { label, classes } = signedDate
      ? { label: 'Signed', classes: 'text-green-700 border-green-300 bg-green-50' }
      : sentDate
        ? { label: 'Delivered', classes: 'text-blue-700 border-blue-300 bg-blue-50' }
        : { label: 'Draft', classes: 'text-muted-foreground border-border bg-muted' };

    return (
      <Badge
        variant="outline"
        className={`shrink-0 gap-1.5 rounded-full px-2 py-0.5 text-xs font-medium ${classes}`}
      >
        <span className="inline-block h-1.5 w-1.5 shrink-0 rounded-full bg-current" />
        {label}
      </Badge>
    );
  };

  if (compact) {
    return (
      <div className="overflow-hidden rounded-lg border border-gray-200 bg-white transition-all hover:shadow-md">
        <button
          type="button"
          onClick={() => setOpen(!open)}
          className="flex w-full items-center gap-3 p-3 text-left hover:bg-gray-50"
        >
          <span className="shrink-0 text-xs font-semibold text-gray-900">P-{proposal.id}</span>
          <span className="min-w-0 flex-1 truncate text-xs text-gray-500">
            {proposal.eventId ? `· Job #${proposal.eventId}` : ''}
          </span>
          {getStatusBadge()}
          <span className="shrink-0 text-xs text-gray-400">{createdDate}</span>
          {isLoadingQuoteLines ? (
            <Skeleton className="h-4 w-14 shrink-0" />
          ) : (
            <span className="shrink-0 text-xs font-semibold text-gray-900">
              ${approvedTotal.toFixed(2)}
            </span>
          )}
          <ChevronDown
            className={`h-4 w-4 shrink-0 text-gray-400 transition-transform ${
              open ? 'rotate-180' : ''
            }`}
          />
        </button>
        {open && (
          <div className="border-t bg-gray-50 p-4">
            <ProposalDetailsView proposal={proposal} />
            <div className="mt-4 flex justify-end">
              <Button variant="outline" size="sm" onClick={() => onViewProposal(proposal.id)}>
                View Proposal
                <EyeIcon className="ml-1.5 h-4 w-4" />
              </Button>
            </div>
          </div>
        )}
      </div>
    );
  }

  return (
    <Card className="overflow-hidden rounded-lg bg-white">
      <CardContent className="p-4">
        <div className="mb-3 flex items-start justify-between gap-2">
          <div className="flex min-w-0 flex-wrap items-center gap-2">
            <h3 className="text-base font-semibold sm:text-lg">P-{proposal.id}</h3>
            {proposal.eventId && (
              <>
                <span className="text-gray-400">•</span>
                <span className="text-sm text-gray-600">Job #{proposal.eventId}</span>
              </>
            )}
          </div>
          {getStatusBadge()}
        </div>

        <div className="mb-3 flex flex-wrap items-center gap-x-2 gap-y-1 text-sm text-gray-500">
          <span>{createdDate}</span>
          {signedDate && (
            <>
              <span className="text-gray-400">•</span>
              <span>Signed: {signedDate}</span>
            </>
          )}
        </div>

        <div className="mb-3 border-t" />

        <div className="flex items-end justify-between gap-2">
          <div>
            <p className="text-xs text-gray-500">Approved Total</p>
            {isLoadingQuoteLines ? (
              <Skeleton className="mt-1 h-6 w-20" />
            ) : (
              <p className="text-lg font-semibold text-gray-900">${approvedTotal.toFixed(2)}</p>
            )}
            {!isLoadingQuoteLines && approvedCount > 0 && (
              <p className="text-xs text-gray-500">
                {approvedCount} {approvedCount === 1 ? 'item' : 'items'}
              </p>
            )}
          </div>
          <Button
            variant="outline"
            size="sm"
            onClick={() => onViewProposal(proposal.id)}
            className="shrink-0"
          >
            View Proposal
          </Button>
        </div>

        {/* Delivery Information - only show if delivered (sent but not necessarily signed) */}
        {sentDate && (
          <p className="mt-3 break-words border-t pt-3 text-xs text-gray-500">
            {proposal.inPerson === 1
              ? 'Shared in person'
              : customer?.email
                ? `Emailed to ${customer.email}`
                : 'Emailed to customer'}
          </p>
        )}
      </CardContent>
    </Card>
  );
};

export const PastProposals: FC<PastProposalsProps> = ({
  proposalsData: proposalsDataProp,
  isLoading: isLoadingProp = false,
  error: errorProp = null,
  jobCustomer = null,
  currentPage,
  onPageChange,
  pageCount,
  variant = 'default',
}) => {
  const [selectedProposalId, setSelectedProposalId] = useState<number | null>(null);
  const compact = variant === 'compact';

  const proposals = useMemo(() => proposalsDataProp?.results || [], [proposalsDataProp?.results]);
  const selectedProposal = proposals.find((p) => p.id === selectedProposalId);
  const totalCount = proposalsDataProp?.totalCount || 0;

  const handleViewProposal = (proposalId: number) => {
    setSelectedProposalId(proposalId);
  };

  const handleCloseDialog = () => {
    setSelectedProposalId(null);
  };

  if (isLoadingProp) {
    return (
      <div className="space-y-4">
        <Skeleton className="h-32 w-full" />
        <Skeleton className="h-32 w-full" />
        <Skeleton className="h-32 w-full" />
      </div>
    );
  }

  if (errorProp) {
    return (
      <Card className="border-red-200 bg-red-50">
        <CardContent className="p-4">
          <p className="text-red-600">Error loading proposals: {(errorProp as Error).message}</p>
        </CardContent>
      </Card>
    );
  }

  return (
    <>
      <div className="space-y-4">
        {proposals.length === 0 && !isLoadingProp ? (
          compact ? (
            <div className="flex flex-col items-center justify-center py-8 text-gray-400">
              <FileTextIcon className="h-8 w-8" />
              <p className="mt-2 text-xs">No proposals found for this property</p>
            </div>
          ) : (
            <Card>
              <CardContent className="p-4 text-center text-gray-500">
                No proposals found for this property
              </CardContent>
            </Card>
          )
        ) : (
          <>
            <div className={compact ? 'space-y-3' : 'space-y-4'}>
              {proposals.map((proposal) => (
                <ProposalCard
                  key={proposal.id}
                  proposal={proposal}
                  onViewProposal={handleViewProposal}
                  customer={jobCustomer}
                  compact={compact}
                />
              ))}
            </div>

            {/* Pagination */}
            {!compact && pageCount > 1 && (
              <div className="flex flex-col gap-3 pt-4 sm:flex-row sm:items-center sm:justify-between">
                <p className="text-xs text-gray-600 sm:text-sm">Total: {totalCount} proposals</p>
                <DataTablePagination
                  currentPage={currentPage}
                  setPage={onPageChange}
                  pageCount={pageCount}
                  disabled={isLoadingProp}
                />
              </div>
            )}
          </>
        )}
      </div>

      {selectedProposal && (
        <ProposalDetailsDialog proposal={selectedProposal} onClose={handleCloseDialog} />
      )}
    </>
  );
};
